/**
 * web/src/shell/overlays.tsx — the overlay/modal host: the Explore menu, the
 * global toasts, the paywall sheet, the pair ceremony and the tour, moved
 * verbatim out of App.tsx (card WUHnArc6lNID). Mount order here is mount
 * order in the app (the ceremony is mounted before the tour on purpose).
 */
import type { Dispatch, SetStateAction } from 'react';
import { MoreMenu, type MorePick } from '../components/MoreMenu';
import { UpdateToast } from '../ui/UpdateToast';
import { ReferralToast } from '../ui/ReferralToast';
import { PromoToast } from '../ui/PromoToast';
import { InterestsNudgeToast } from '../ui/InterestsNudgeToast';
import { UpgradeSheet } from '../components/UpgradeSheet';
import { PairCeremony } from '../components/PairCeremony';
import { Onboarding } from '../components/Onboarding';
import type { useSession } from '../hooks/useSession';

interface OverlayHostProps {
  onChat: boolean;
  session: ReturnType<typeof useSession>;
  moreOpen: boolean;
  setMoreOpen: Dispatch<SetStateAction<boolean>>;
  handleMorePick: (pick: MorePick) => void;
  signOut: () => Promise<void>;
  tourOpen: boolean;
  setTourOpen: Dispatch<SetStateAction<boolean>>;
}

export function OverlayHost({ onChat, session, moreOpen, setMoreOpen, handleMorePick, signOut, tourOpen, setTourOpen }: OverlayHostProps) {
  return (
    <>
    {/* The full menu — every screen and sub-view as a quick link, Settings
        inside (card yOcdjnR6pXBz). */}
    <MoreMenu open={moreOpen} onClose={() => setMoreOpen(false)} onPick={handleMorePick} onSignOut={() => void signOut()} />

    {/* App-update notice (card 7bLlo0He2hW0) — a bottom toast when this
        navigation handed over a newer build, instead of a hard reload. */}
    <UpdateToast />
    <ReferralToast />
    <PromoToast />
    {/* Interests nudge (card mMl44G43YK5X) — first-login-only toast; it
        reads its own eligibility (first-act undone, profile incomplete) and
        waits for the ceremony/first-act/tour overlays to clear, so mounting
        it beside the other toasts costs nothing on every later login. */}
    {onChat && <InterestsNudgeToast />}

    {/* Pear Plus paywall (card 32BEnNO6I2kx) — listens for the global
        pear:plus-required event that lib/api.ts fires on a 402, so every
        gated tap anywhere opens the same sheet. */}
    <UpgradeSheet />

    {/* The ceremonial pairing moment (card 2NuQ8dd2JMqs) — portalled to body,
        and mounted BEFORE the tour: both take the one shared overlay slot
        (UX item 6), so mount order decides who goes first. A newly paired
        couple sees the celebration, and the tour is granted the slot when it
        fades. Both devices get it — the partner from the room's `paired`
        frame, the joiner from its join response. */}
    {session.celebrate && <PairCeremony onDone={session.clearCelebrate} />}

    {/* The concierge's walk-through — portalled to body (see Onboarding.tsx). */}
    {tourOpen && <Onboarding onClose={() => setTourOpen(false)} />}
    </>
  );
}
